import { useMutation } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { transferSwiftReturnTransferMutation, transferWireReverseWireTransferMutation } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@column/ui';

import { NotificationList } from '~/components';
import { RenderFields } from '~/components/RenderFields';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { getErrorMessage } from '~/lib/api/getErrorMessage';
import { WireReturnReasonOption, swiftReturnReasons, wireReturnReasons } from '~/lib/transfers/returnReasons';
import { useNotificationStore } from '~/stores/Notification';

export type WireReturnTransferType = 'wire' | 'swift';

interface WireReturnModalProps {
  transferId: string;
  transferType: WireReturnTransferType;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface WireReturnForm {
  reason: string;
  message: string;
}

const config = {
  wire: { reasons: wireReturnReasons, messageLabel: 'Description', messageMaxLength: 255 },
  swift: { reasons: swiftReturnReasons, messageLabel: 'Message to Originator Bank', messageMaxLength: undefined },
} satisfies Record<
  WireReturnTransferType,
  { reasons: WireReturnReasonOption[]; messageLabel: string; messageMaxLength?: number }
>;

const defaultValues: WireReturnForm = { reason: '', message: '' };

export const WireReturnModal = ({ transferId, transferType, isOpen, onClose, onSuccess }: WireReturnModalProps) => {
  const { addDangerNotification, addSuccessNotification, clearNotifications } = useNotificationStore();
  const { handleSubmitErrors } = useFormErrorHandler({ defaultDisplayType: 'popup' });
  const { reasons, messageLabel, messageMaxLength } = config[transferType];

  const formMethods = useForm<WireReturnForm>({ defaultValues });

  useEffect(() => {
    if (isOpen) {
      formMethods.reset(defaultValues);
      clearNotifications('popup');
    }
  }, [clearNotifications, formMethods, isOpen]);

  const mutationCallbacks = {
    onSuccess: () => {
      addSuccessNotification({ content: 'Return initiated' });
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => addDangerNotification({ content: getErrorMessage(error), display: 'popup' }),
  };
  const reverseWire = useMutation({ ...transferWireReverseWireTransferMutation(), ...mutationCallbacks });
  const returnSwift = useMutation({ ...transferSwiftReturnTransferMutation(), ...mutationCallbacks });

  const isPending = reverseWire.isPending || returnSwift.isPending;

  const onSubmit = formMethods.handleSubmit(({ reason, message }) => {
    const trimmedMessage = message.trim() || undefined;

    if (transferType === 'wire') {
      reverseWire.mutate({ path: { wire_transfer_id: transferId }, body: { reason, description: trimmedMessage } });
      return;
    }

    returnSwift.mutate({
      path: { swift_transfer_id: transferId },
      body: { return_reason: reason, message_to_originator_bank: trimmedMessage },
    });
  }, handleSubmitErrors);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isPending && onClose()}>
      <DialogContent
        overlayScroll
        showCloseButton={false}
        className="gap-3 sm:max-w-md"
        onOpenAutoFocus={(event) => {
          // The header close button comes first in the DOM; start on the reason picker instead.
          event.preventDefault();
          (event.currentTarget as HTMLElement).querySelector<HTMLElement>('[data-slot="select-trigger"]')?.focus();
        }}
      >
        <DialogHeader className="-mx-6 -mt-2 flex-row items-start justify-between gap-4 border-b border-gray-100 px-6 pb-4 dark:border-white/[0.08]">
          <DialogTitle>Return Transfer</DialogTitle>
          <DialogClose asChild>
            {/* 24px hit area trimmed to the title's 20px line height so the icon centers on the text. */}
            <button
              type="button"
              aria-label="Close"
              disabled={isPending}
              className="-my-0.5 -mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-white/10"
            >
              <Icon.Close className="[--icon-size-x:16px] [--icon-size-y:16px]" />
            </button>
          </DialogClose>
        </DialogHeader>

        {/* Inside the dialog so errors stack above its overlay. */}
        <NotificationList display="popup" spacing="bottom" className="!m-0" />

        <FormProvider {...formMethods}>
          <form autoComplete="off" onSubmit={onSubmit} className="-mx-6 -mt-6 [--page-margin:24px]">
            {RenderFields<WireReturnForm>({
              sections: [
                {
                  fields: [
                    {
                      id: 'reason',
                      label: 'Reason',
                      oneColumn: true,
                      rules: { required: 'Select a reason for the return.' },
                      children: ({ value, onChange }, { error }) => (
                        <Select value={String(value ?? '')} onValueChange={onChange} disabled={isPending}>
                          <SelectTrigger className="w-full" aria-invalid={!!error}>
                            <SelectValue placeholder="Select a reason" />
                          </SelectTrigger>
                          <SelectContent>
                            {reasons.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ),
                    },
                    {
                      id: 'message',
                      label: messageLabel,
                      oneColumn: true,
                      rules: messageMaxLength
                        ? {
                            maxLength: {
                              value: messageMaxLength,
                              message: `${messageLabel} must be ${messageMaxLength} characters or fewer.`,
                            },
                          }
                        : undefined,
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <Textarea
                          placeholder="Optional"
                          aria-invalid={!!error}
                          disabled={isPending}
                          maxLength={messageMaxLength}
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      ),
                    },
                  ],
                },
              ],
            })}
          </form>
        </FormProvider>

        <DialogFooter className="sm:justify-between">
          <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" type="button" onClick={onSubmit} disabled={isPending}>
            <Icon.Return className="[--icon-size-x:16px] [--icon-size-y:16px]" />
            {isPending ? 'Returning…' : 'Return'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
